import React, { useRef } from "react";
import { useUscSelector } from "../../../../redux/store";
import { UscLabel, useUscLabel } from "../../../../utils/labels";
import * as Styles from "./Carousel.styles";
import NavigationSlider from "./NavigationSlider";
import CarouselImage from "./CarouselImage";
import { handleScroll, handleSlideButton } from "../../../../utils/sliderUtils";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import Button from "../../styles/atoms/Button";
import Icon from "../../styles/Icon";
import FullscreenCarousel from "./fullscreen/FullscreenCarousel";
import useCarousel from "../../../../hooks/useCarousel";

const Carousel = (): JSX.Element | null => {
    const commonSettings = useUscSelector((state) => state.commonSettings);

    const sliderRef = useRef<HTMLDivElement>(null);
    const scrollEnabled = useRef(false);

    const isRtl = isRTL(commonSettings);

    const {
        amountOfImages,
        images,
        isFirstElement,
        isLastElement,
        altTag,
        isMobile,
        hasPromotion,
        isReserved,
        isSold,
        sharedSliderIndex,
        isFullscreen,
        setIsFullscreen,
        shouldStartLoading,
        updateSharedSliderIndex,
        openFullScreen,
    } = useCarousel(false, scrollEnabled);

    const [ariaSlidePreviousLabel, ariaSlideNextLabel, ariaFullScreenLabel] = useUscLabel([
        "uscAriaSlidePrevious",
        "uscAriaSlideNext",
        "uscAriaFullScreen",
    ]);

    if (!images.length) return null;

    return (
        <Styles.Slider>
            <Styles.SliderViewport
                scrollable={amountOfImages > 1}
                ref={sliderRef}
                onScroll={() => scrollEnabled.current && updateSharedSliderIndex(handleScroll(sliderRef, isRtl))}
                onMouseEnter={() => {
                    scrollEnabled.current = true;
                }}
                onTouchStart={() => {
                    scrollEnabled.current = true;
                }}
                onMouseLeave={() => {
                    scrollEnabled.current = false;
                }}
            >
                {images.map(({ src }, index) => (
                    <CarouselImage
                        key={index} // eslint-disable-line react/no-array-index-key
                        index={index}
                        src={src}
                        altTag={altTag}
                        amountOfImages={images.length}
                        onClick={isMobile ? undefined : openFullScreen}
                        startLoading={shouldStartLoading(index)}
                    />
                ))}
            </Styles.SliderViewport>

            <Styles.Content>
                <Styles.Actions>
                    <Styles.Badges>
                        {isReserved && (
                            <Styles.Reserved>
                                <UscLabel label="uscPdpStatusReserved" />
                            </Styles.Reserved>
                        )}
                        {hasPromotion && (
                            <Styles.Reserved>
                                <UscLabel label="uscPdpStatusPromotion" />
                            </Styles.Reserved>
                        )}
                        {isSold && (
                            <Styles.Reserved>
                                <UscLabel label="uscPdpStatusSold" />
                            </Styles.Reserved>
                        )}
                    </Styles.Badges>
                </Styles.Actions>
            </Styles.Content>

            <Styles.CarouselButtons>
                <Styles.SliderButtons hidden={amountOfImages <= 1}>
                    <Button
                        variant="control"
                        disabled={isFirstElement}
                        onClick={(event) => {
                            // Make sure to not open the fullscreen
                            event.stopPropagation();
                            updateSharedSliderIndex(handleSlideButton("left", sharedSliderIndex, amountOfImages));
                        }}
                        aria-label={ariaSlidePreviousLabel}
                    >
                        <Icon variant="chevron-left" />
                    </Button>
                    <Button
                        variant="control"
                        disabled={isLastElement}
                        onClick={(event) => {
                            // Make sure to not open the fullscreen
                            event.stopPropagation();
                            updateSharedSliderIndex(handleSlideButton("right", sharedSliderIndex, amountOfImages));
                        }}
                        aria-label={ariaSlideNextLabel}
                    >
                        <Icon variant="chevron-right" />
                    </Button>
                </Styles.SliderButtons>
                <Styles.FullScreenButton>
                    <Button variant="control-negative" onClick={openFullScreen} aria-label={ariaFullScreenLabel}>
                        <Icon variant="fullscreen" />
                    </Button>
                </Styles.FullScreenButton>
            </Styles.CarouselButtons>

            <FullscreenCarousel
                sharedSliderIndex={sharedSliderIndex}
                images={images}
                isFullscreen={isFullscreen}
                scrollEnabled={scrollEnabled}
                onClose={() => setIsFullscreen(false)}
                updateSharedSliderIndex={updateSharedSliderIndex}
            />

            <NavigationSlider
                images={images}
                showArrowButtons={false}
                sharedSliderIndex={sharedSliderIndex}
                updateSharedSliderIndex={updateSharedSliderIndex}
                disableScroll={() => {
                    scrollEnabled.current = false;
                }}
            />
        </Styles.Slider>
    );
};

export default Carousel;
